import { useNavigate, useParams } from '@tanstack/react-router';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@/lib/auth/auth-store';
import { confirmEmailChange } from '@/lib/auth/security-service';
import { toast } from '@/lib/toast';

type ConfirmationState = 'processing' | 'success' | 'error';

export default function ConfirmEmailChange() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useParams({ strict: false });
  const login = useAuthStore((state) => state.login);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const [state, setState] = useState<ConfirmationState>('processing');

  useEffect(() => {
    const confirmEmail = async () => {
      if (!token) {
        setState('error');
        return;
      }

      const result = await confirmEmailChange(token);

      if (result.success) {
        login(result.data.token);
        setState('success');
        toast.success('settings.security.emailConfirmation.success');
      } else {
        setState('error');
      }
    };

    confirmEmail();
  }, [token, login]);

  const handleNavigate = () => {
    if (state === 'success' && isAuthenticated) {
      navigate({ to: '/dashboard/settings', search: { tab: 'security' } });
    } else {
      navigate({ to: '/login' });
    }
  };

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="w-full max-w-md rounded-xl bg-surface-card p-8 text-center shadow-lg">
        {state === 'processing' && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-accent-primary" />
            <h1 className="mt-6 font-heading text-xl font-semibold text-foreground-primary">
              {t('settings.security.emailConfirmation.title')}
            </h1>
            <p className="mt-2 text-foreground-secondary">
              {t('settings.security.emailConfirmation.processing')}
            </p>
          </>
        )}

        {state === 'success' && (
          <>
            <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
            <h1 className="mt-6 font-heading text-xl font-semibold text-foreground-primary">
              {t('settings.security.emailConfirmation.success')}
            </h1>
            <p className="mt-2 text-foreground-secondary">
              {t('settings.security.emailConfirmation.successMessage')}
            </p>
            <button
              type="button"
              onClick={handleNavigate}
              className="mt-6 rounded-lg bg-accent-primary px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-accent-hover"
            >
              {isAuthenticated
                ? t('settings.security.emailConfirmation.goToSettings')
                : t('settings.security.emailConfirmation.goToLogin')}
            </button>
          </>
        )}

        {state === 'error' && (
          <>
            <XCircle className="mx-auto h-12 w-12 text-red-500" />
            <h1 className="mt-6 font-heading text-xl font-semibold text-foreground-primary">
              {t('settings.security.emailConfirmation.error')}
            </h1>
            <p className="mt-2 text-foreground-secondary">
              {t('settings.security.emailConfirmation.errorMessage')}
            </p>
            <button
              type="button"
              onClick={handleNavigate}
              className="mt-6 rounded-lg bg-accent-primary px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-accent-hover"
            >
              {isAuthenticated
                ? t('settings.security.emailConfirmation.goToSettings')
                : t('settings.security.emailConfirmation.goToLogin')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
